// src/prototype/enterprise/scenarios/ScenariosSurface.tsx
// ============================================================
// ENTERPRISE · SCENARIOS SURFACE (Surface B) — Part A: levers + live re-rank + funding line.
//
// Lever rail (capital cap + 4 strategic weights + Q2 preset / reset) drives net-new client-side
// compute (scenarioModel) over the Q1 baseline read from the spine. Dragging any lever re-scores
// and re-ranks the portfolio live and moves the cumulative funding line. Save + Compare are
// Part B (present but disabled here). Layout/styling match Enterprise Prototype.dc.html · Surface B.
//
// PORTFOLIO-LEVEL scenario modeling — distinct from per-use-case ROI scenarios.
// ============================================================

import React, { useMemo, useState } from 'react';
import { initiativeName } from '../portfolio/portfolioDisplay';
import { GovernanceModal } from '../governance/GovernanceModal';
import {
  loadScenarioBase, computeScenario, computeCompare, normalizedWeights, sameLevers,
  loadSavedScenario, persistSavedScenario,
  BASELINE_WEIGHTS, Q2_WEIGHTS, BASELINE_CAP,
  type ScenarioWeights, type ScenRow, type SavedScenario, type CompareRow,
} from './scenarioModel';

const CAP_MIN = 20;
const CAP_MAX = 60;
const WEIGHT_MIN = 5;
const WEIGHT_MAX = 60;

const isBaselineState = (w: ScenarioWeights, cap: number) =>
  cap === BASELINE_CAP && w.fi === 25 && w.ou === 25 && w.sa === 25 && w.if === 25;
const isQ2State = (w: ScenarioWeights, cap: number) =>
  cap === 40 && w.fi === Q2_WEIGHTS.fi && w.ou === Q2_WEIGHTS.ou && w.sa === Q2_WEIGHTS.sa && w.if === Q2_WEIGHTS.if;

const WEIGHT_ROWS: { key: keyof ScenarioWeights; name: string }[] = [
  { key: 'fi', name: 'Financial Impact' },
  { key: 'ou', name: 'Operational Urgency' },
  { key: 'sa', name: 'Strategic Alignment' },
  { key: 'if', name: 'Implementation Feasibility' },
];

const tagFor = (r: ScenRow): { label: string; cls: string } => {
  if (r.crossing) return { label: '⤓ crossing', cls: 'bg-[#fef3c7] text-[#b45309]' };
  if (r.atLine) return { label: 'at the line', cls: 'bg-[#fef3c7] text-[#b45309]' };
  if (r.funded) return { label: 'funded', cls: 'bg-[#dcfce7] text-[#15803d]' };
  return { label: 'unfunded', cls: 'bg-[#f1f3f6] text-[#9ca3af]' };
};

const COMPARE_COLS = '1fr 104px 72px 148px 108px 84px';

const deltaFor = (c: CompareRow): { label: string; cls: string } => {
  const rank = c.rankDelta > 0 ? `▲${c.rankDelta}` : c.rankDelta < 0 ? `▼${-c.rankDelta}` : '—';
  const label = `${c.compDelta >= 0 ? '+' : '−'}${Math.abs(c.compDelta).toFixed(1)} · rank ${rank}${c.crossed ? ' ⤓' : ''}`;
  const cls = c.crossed ? 'bg-[#fef3c7] text-[#b45309]' : c.compDelta >= 0 ? 'bg-[#dcfce7] text-[#15803d]' : 'bg-[#fee2e2] text-[#b91c1c]';
  return { label, cls };
};

export const ScenariosSurface: React.FC = () => {
  const base = useMemo(() => loadScenarioBase(), []);
  const [cap, setCap] = useState<number>(base.baselineCap);
  const [weights, setWeights] = useState<ScenarioWeights>({ ...BASELINE_WEIGHTS });
  const [view, setView] = useState<'levers' | 'compare'>('levers');
  const [saved, setSaved] = useState<SavedScenario | null>(() => loadSavedScenario());
  const [govOpen, setGovOpen] = useState(false);

  const result = useMemo(() => computeScenario(base, weights, cap), [base, weights, cap]);
  const compare = useMemo(() => computeCompare(base, weights, cap), [base, weights, cap]);
  const pct = normalizedWeights(weights);
  const minWeight = Math.min(weights.fi, weights.ou, weights.sa, weights.if);

  const baseline = isBaselineState(weights, cap);
  const q2 = isQ2State(weights, cap);
  // Saved-state badge: matches the saved levers → "Saved"; saved but edited → "Unsaved changes".
  const savedCurrent = !!saved && sameLevers(saved.weights, saved.cap, weights, cap);
  const scenTitle = savedCurrent ? saved!.name : baseline ? 'Q1 baseline' : q2 ? 'Q2 Budget Reduction' : 'Unsaved scenario';
  const savedBadge = savedCurrent ? `Saved · ${saved!.name}` : saved ? 'Unsaved changes' : 'Unsaved · live';
  const savedBadgeCls = savedCurrent ? 'bg-[#dcfce7] text-[#15803d]' : 'bg-[#fef3c7] text-[#b45309]';

  const scenarioName = (): string =>
    isQ2State(weights, cap) ? 'Q2 Budget Reduction' : isBaselineState(weights, cap) ? 'Q1 baseline (saved)' : 'Custom scenario';
  const saveScenario = () => {
    const rec: SavedScenario = { name: scenarioName(), cap, weights };
    persistSavedScenario(rec);
    setSaved(rec);
  };

  const capDelta = Math.round((cap - base.baselineCap) * 10) / 10;
  const capDeltaLabel =
    capDelta === 0 ? '= Q1 baseline' : `${capDelta < 0 ? '−' : '+'}$${Math.abs(capDelta).toFixed(1)}M (${capDelta < 0 ? '' : '+'}${Math.round((capDelta / base.baselineCap) * 100)}%)`;

  const applyQ2 = () => { setWeights({ ...Q2_WEIGHTS }); setCap(40); };
  const resetQ1 = () => { setWeights({ ...BASELINE_WEIGHTS }); setCap(base.baselineCap); };
  const setWeight = (key: keyof ScenarioWeights, v: number) => setWeights((w) => ({ ...w, [key]: v }));

  // Focus the view on the funding line — a window of rows centered on where crossings happen.
  const N = result.fundedCount;
  const from = Math.max(0, N - 8);
  const to = Math.min(result.rows.length, N + 8);
  const windowRows = result.rows.slice(from, to);

  // ── Compare vs Q1 baseline (Part B) ──
  if (view === 'compare') {
    const c = compare;
    return (
      <div className="min-h-[calc(100vh-180px)] bg-[#F5F7FA]">
        <div className="px-6 py-6">
          <div className="bg-white border border-[#e5e7eb] rounded-xl overflow-hidden">
            {/* Header */}
            <div className="flex justify-between items-center px-5 py-3.5 border-b border-[#e5e7eb]">
              <div>
                <div className="text-base font-bold text-[#18365E]">Compare scenarios</div>
                <div className="text-[11px] text-[#717182] mt-0.5">{scenTitle} vs Q1 2026 Planning (base)</div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setView('levers')}
                  className="border border-[#e5e7eb] rounded-lg px-3 py-1.5 text-[11.5px] font-semibold text-[#374151] bg-white hover:bg-[#f8fafc]"
                >
                  ← Back to levers
                </button>
                <button
                  onClick={() => setGovOpen(true)}
                  className="bg-[#18365E] text-white rounded-lg px-3 py-1.5 text-[11.5px] font-semibold hover:bg-[#122a49]"
                >
                  Route to governance →
                </button>
              </div>
            </div>

            {/* Base · Δ · Scenario summary */}
            <div className="grid border-b border-[#e5e7eb]" style={{ gridTemplateColumns: '1fr 110px 1fr' }}>
              <div className="px-5 py-3 border-r border-[#e5e7eb]">
                <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#717182]">Base · Q1 2026 Planning</div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-[11px] text-[#374151]">
                  <span>Cap <b className="text-[#18365E]">${c.base.cap.toFixed(1)}M</b></span>
                  <span>Weights <b className="text-[#18365E]">25 · 25 · 25 · 25</b></span>
                  <span>Funded <b className="text-[#18365E]">{c.base.fundedCount}</b></span>
                </div>
              </div>
              <div className="flex items-center justify-center text-[10px] font-bold uppercase tracking-[0.1em] text-[#b45309] bg-[#fffbeb] border-r border-[#e5e7eb]">
                Δ vs base
              </div>
              <div className="px-5 py-3 bg-[#fff7f4]">
                <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#FF6E4C]">Scenario · {scenTitle}</div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-[11px] text-[#374151]">
                  <span>
                    Cap <b className="text-[#FF6E4C]">${c.scenario.cap.toFixed(1)}M</b>
                    {capDelta !== 0 && <b className="text-[#b91c1c]"> {capDeltaLabel}</b>}
                  </span>
                  <span>Weights <b className="text-[#FF6E4C]">{pct.fi} · {pct.ou} · {pct.sa} · {pct.if}</b></span>
                  <span>Funded <b className="text-[#FF6E4C]">{c.scenario.fundedCount}</b></span>
                </div>
              </div>
            </div>

            {/* Movers / crossings table */}
            <div className="overflow-x-auto">
              <div style={{ minWidth: 760 }}>
                <div
                  className="grid px-5 py-2 border-b border-[#e5e7eb] text-[9px] font-bold uppercase tracking-[0.07em] text-[#717182]"
                  style={{ gridTemplateColumns: COMPARE_COLS, columnGap: 8 }}
                >
                  <div>Initiative</div>
                  <div className="text-center">Base rank · comp</div>
                  <div className="text-center">Base funded</div>
                  <div className="text-center">Δ vs base</div>
                  <div className="text-center">Scen rank · comp</div>
                  <div className="text-center">Scen funded</div>
                </div>

                {c.rows.length === 0 ? (
                  <div className="px-5 py-6 text-center text-[11px] text-[#9ca3af]">
                    No movement — this scenario matches the Q1 baseline. Adjust a lever to see what re-prioritizes.
                  </div>
                ) : (
                  c.rows.map((row, i) => {
                    const d = deltaFor(row);
                    return (
                      <div
                        key={row.id}
                        className="grid items-center px-5 py-2 border-b border-[#f1f3f6]"
                        style={{ gridTemplateColumns: COMPARE_COLS, columnGap: 8, background: row.crossed ? '#fffdf5' : i % 2 ? '#FAFBFC' : '#fff' }}
                      >
                        <div className="min-w-0 truncate">
                          <span className="text-xs font-semibold text-[#18365E]">{initiativeName(row.title)}</span>
                          <span className="text-[9.5px] text-[#9ca3af] ml-2">{row.owner.name} · {row.owner.org}</span>
                        </div>
                        <div className="text-center text-[11px] text-[#374151]">#{row.baseRank} · {row.baseComp.toFixed(1)}</div>
                        <div className="text-center text-[11px]">{row.baseFunded ? '✓' : '—'}</div>
                        <div className="text-center">
                          <span className={`inline-block rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${d.cls}`}>{d.label}</span>
                        </div>
                        <div className="text-center text-[11px] font-semibold text-[#18365E]">#{row.scenRank} · {row.scenComp.toFixed(1)}</div>
                        <div className="text-center text-[11px]" style={{ color: row.crossed ? '#b91c1c' : '#374151' }}>
                          {row.crossed ? '✗ crossed' : row.scenFunded ? '✓' : '—'}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <div className="px-5 py-2.5 text-[10.5px] text-[#9ca3af] bg-[#F5F7FA]">
              Largest movers and funding-line crossings shown · base / Δ / scenario columns (ROI-command-center salvage shape)
            </div>
          </div>
        </div>
        {govOpen && <GovernanceModal onClose={() => setGovOpen(false)} />}
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-180px)] bg-[#F5F7FA]">
      <div className="px-6 py-6">
        <div className="bg-white border border-[#e5e7eb] rounded-xl flex overflow-hidden">
          {/* ── Lever rail ── */}
          <div className="w-[300px] flex-none border-r border-[#e5e7eb] bg-[#F5F7FA] px-5 py-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#717182]">Scenario levers</div>
            <div className="text-[13px] font-bold text-[#18365E] mt-1 mb-3 flex items-center gap-2">
              {scenTitle}
              <span className={`text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${savedBadgeCls}`}>
                {savedBadge}
              </span>
            </div>

            <div className="flex gap-1.5 mb-3">
              <button onClick={applyQ2} className="flex-1 bg-[#18365E] text-white rounded-md px-2 py-1.5 text-[10px] font-semibold hover:bg-[#122a49]">
                Apply Q2 cut (−20%)
              </button>
              <button onClick={resetQ1} className="flex-1 border border-[#d1d5db] bg-white text-[#475569] rounded-md px-2 py-1.5 text-[10px] font-semibold hover:bg-[#f8fafc]">
                Reset to Q1
              </button>
            </div>

            {/* Capital cap */}
            <div className="bg-white border border-[#e5e7eb] rounded-lg px-3.5 py-3 mb-3">
              <div className="flex justify-between text-[10.5px] font-semibold text-[#374151] mb-2">
                <span>Capital cap</span>
                <span className="text-[#FF6E4C] font-bold">${cap.toFixed(1)}M</span>
              </div>
              <input
                type="range" min={CAP_MIN} max={CAP_MAX} step={1} value={cap}
                onChange={(e) => setCap(+e.target.value)}
                className="w-full accent-[#FF6E4C]"
              />
              <div className="flex justify-between text-[9px] text-[#9ca3af] mt-1">
                <span>${CAP_MIN}M</span>
                <span className={capDelta === 0 ? 'text-[#18365E] font-semibold' : 'text-[#b91c1c] font-semibold'}>{capDeltaLabel}</span>
                <span>${CAP_MAX}M</span>
              </div>
            </div>

            {/* Strategic weights */}
            <div className="bg-white border border-[#e5e7eb] rounded-lg px-3.5 py-3">
              <div className="text-[10.5px] font-semibold text-[#374151] mb-2">
                Strategic weights <span className="text-[9px] text-[#9ca3af] font-normal">normalize to 100%</span>
              </div>
              {WEIGHT_ROWS.map(({ key, name }) => (
                <div key={key} className="mb-2 last:mb-0">
                  <div className="flex justify-between text-[10px] text-[#374151]">
                    <span>{name}</span>
                    <span className="font-bold" style={{ color: weights[key] > minWeight ? '#FF6E4C' : '#18365E' }}>{pct[key]}%</span>
                  </div>
                  <input
                    type="range" min={WEIGHT_MIN} max={WEIGHT_MAX} step={1} value={weights[key]}
                    onChange={(e) => setWeight(key, +e.target.value)}
                    className="w-full accent-[#FF6E4C]"
                  />
                </div>
              ))}
            </div>

            {/* Save / Compare */}
            <button
              onClick={saveScenario} disabled={savedCurrent}
              className={`mt-3.5 w-full text-white text-center rounded-lg py-2.5 text-xs font-bold ${
                savedCurrent ? 'bg-[#15803d]/70 cursor-default' : 'bg-[#FF6E4C] hover:bg-[#f2593a]'
              }`}
            >
              {savedCurrent ? 'Saved ✓' : 'Save scenario'}
            </button>
            <button
              onClick={() => setView('compare')}
              className="mt-2 w-full border-[1.5px] border-[#18365E] text-[#18365E] text-center rounded-lg py-2 text-[11.5px] font-semibold bg-white hover:bg-[#18365E]/[0.04]"
            >
              Compare vs Q1 baseline →
            </button>

            <div className="mt-3.5 border-t border-dashed border-[#d1d5db] pt-2.5 text-[9.5px] leading-relaxed text-[#9ca3af]">
              Portfolio-level scenario — distinct from per-use-case ROI scenarios (conservative / base / aggressive), which stay on the initiative's ROI page.
            </div>
          </div>

          {/* ── Ranked portfolio (re-prioritizing live) ── */}
          <div className="flex-1 min-w-0">
            <div className="flex justify-between items-center px-5 py-3 border-b border-[#e5e7eb]">
              <div className="text-sm font-bold text-[#18365E]">Ranked portfolio · re-prioritizing live</div>
              <div className="flex gap-3.5 text-[11px] text-[#374151]">
                <span><b className="text-[#15803d]">{result.fundedCount}</b> funded</span>
                <span><b className="text-[#b45309]">{result.crossingCount}</b> crossing below</span>
                <span><b className="text-[#18365E]">${result.allocated.toFixed(1)}M</b> of ${cap.toFixed(1)}M allocated</span>
              </div>
            </div>
            <div className="px-5 py-1.5 text-[10.5px] text-[#717182] bg-[#F5F7FA] border-b border-[#e5e7eb]">
              Showing initiatives {from + 1}–{to} of {result.rows.length}, focused on the funding line · full list in the Portfolio tab
            </div>

            {windowRows.map((r) => {
              const tag = tagFor(r);
              const showLineAfter = r.rank === N && N > 0; // funding-line divider sits below the last funded row
              return (
                <React.Fragment key={r.id}>
                  <div
                    className="flex items-center gap-3 px-5 py-[7px] border-b border-[#f1f3f6]"
                    style={{ background: r.crossing ? '#fffdf5' : '#fff' }}
                  >
                    <span className="text-[10px] font-bold w-[34px] shrink-0" style={{ color: r.move > 0 ? '#15803d' : r.move < 0 ? '#b91c1c' : '#c3c9d2' }}>
                      {r.move > 0 ? `▲ ${r.move}` : r.move < 0 ? `▼ ${-r.move}` : '—'}
                    </span>
                    <span className="w-[30px] h-[22px] shrink-0 rounded-full text-[10px] font-bold flex items-center justify-center"
                      style={{ background: r.crossing ? '#fef3c7' : '#18365E', color: r.crossing ? '#b45309' : '#fff' }}>
                      {r.rank}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-[#18365E] truncate">{initiativeName(r.title)}</div>
                      <div className="text-[9.5px] text-[#9ca3af] truncate">{r.category} · {r.owner.name} · {r.owner.org}</div>
                    </div>
                    <span className="text-[10.5px] text-[#374151] w-[56px] text-right shrink-0">${r.ask.toFixed(1)}M</span>
                    <span className="text-xs font-bold text-[#18365E] w-[42px] text-right shrink-0">{r.comp.toFixed(1)}</span>
                    <span className={`text-[10px] font-semibold rounded-full px-2.5 py-0.5 shrink-0 ${tag.cls}`}>{tag.label}</span>
                  </div>
                  {showLineAfter && (
                    <div className="flex items-center gap-3 px-5 py-[7px] bg-[#18365E]">
                      <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-white">Funding line</span>
                      <div className="flex-1 h-0.5" style={{ background: 'repeating-linear-gradient(90deg,#FF6E4C 0 8px,transparent 8px 14px)' }} />
                      <span className="text-[10.5px] text-[#b7c4d8]">${result.allocated.toFixed(1)}M of ${cap.toFixed(1)}M committed · {result.fundedCount} funded</span>
                    </div>
                  )}
                </React.Fragment>
              );
            })}

            <div className="px-5 py-2.5 text-[10.5px] text-[#9ca3af] bg-[#F5F7FA]">
              Drag the levers — rows re-rank and cross the line live. Amber rows are funded at Q1 but unfunded here.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScenariosSurface;
